/* eslint-disable */
import { useHumanInTheLoop } from "@copilotkit/react-core/v2";
import { z } from "zod";
import { resolveSupportedAppTypes, checkIfSourceEnabled, checkIfTargetEnabled } from "../utils/supported-app-types";
// import { TQLEditorTool, type TQLAutoFix } from "./tql-tools/tql-editor-tool";
// import { TQLEditorToolV2 as TQLEditorTool, type TQLAutoFixV2 as TQLAutoFix } from "./tql-tools/tql-editor-tool-v2";
import { TQLEditorToolV3 as TQLEditorTool } from "./tql-tools/tql-editor-tool-v3";
import { NameAppCheckerTool } from "./tql-tools/name-app-checker-tool";
import { AskILOrCDC } from "./tql-tools/ask-il-or-cdc";
import { SourceConfiguratorTool } from "./tql-tools/source-configurator-tool";
import { ShowSimpleWizardFormTool } from "./tql-tools/show-simple-wizard-form-tool";
import { ShowBasicTargetFormTool } from "./tql-tools/show-basic-target-form-tool";
import { TargetConfiguratorTool } from "./tql-tools/target-configurator-tool";
import { HitlSummary } from "./hitl-summary";
// @ts-ignore
import TARGET_FORM_INFO from "src/modules/wizards/target/target-form-info";
import { ADAPTERS_WITH_RICH_WIZARD } from "app/components/appwizard/component-wizards/component-wizards";

const SUPPORTED_TARGET_TYPES = Object.keys(TARGET_FORM_INFO).join(", ");
const SUPPORTED_SOURCE_TYPES = ["ORACLE", "OJET", "POSTGRES", "SQLSERVER", "MSJET", "MYSQL", "MARIADB"] as const;
const RICH_WIZARD_TARGET_TYPES_LABEL = (ADAPTERS_WITH_RICH_WIZARD as string[]).join(" or ");
// export type { TQLEditorToolProps } from "./tql-tools/tql-editor-tool";
// export { TQLEditorTool } from "./tql-tools/tql-editor-tool";
// export type { TQLEditorToolV2Props as TQLEditorToolProps } from "./tql-tools/tql-editor-tool-v2";
// export { TQLEditorToolV2 as TQLEditorTool } from "./tql-tools/tql-editor-tool-v2";
export type { TQLEditorToolV3Props as TQLEditorToolProps } from "./tql-tools/tql-editor-tool-v3";
export { TQLEditorToolV3 as TQLEditorTool } from "./tql-tools/tql-editor-tool-v3";
export type { NameAppCheckerToolProps } from "./tql-tools/name-app-checker-tool";
export type { AskILOrCDCProps } from "./tql-tools/ask-il-or-cdc";
export type { SourceConfiguratorToolProps } from "./tql-tools/source-configurator-tool";
export type { TargetConfiguratorToolProps } from "./tql-tools/target-configurator-tool";

export { NameAppCheckerTool } from "./tql-tools/name-app-checker-tool";
export { AskILOrCDC } from "./tql-tools/ask-il-or-cdc";
export { SourceConfiguratorTool } from "./tql-tools/source-configurator-tool";
export { ShowSimpleWizardFormTool } from "./tql-tools/show-simple-wizard-form-tool";
export { ShowBasicTargetFormTool } from "./tql-tools/show-basic-target-form-tool";
export { TargetConfiguratorTool } from "./tql-tools/target-configurator-tool";


export const useTQLTools = () => {
    useHumanInTheLoop({
        name: "showEditorForTQL",
        description: "Displays a TQL editor to the user so they can review or edit the generated TQL.",
        parameters: z.object({
            tql: z.string().describe("The TQL query to show in the editor"),
            isTransient: z
                .boolean()
                .optional()
                .default(false)
                .describe("Set true for an intermediate step that doesn't need user review — the editor is skipped and the skill advances to its next step."),
        }),
        render: ({ args, respond, status, name, result }: { args: { tql?: string; isTransient?: boolean }; respond: (r: string) => void; status: string; name: string; result?: string }) => {
            if (status === "complete") return <HitlSummary toolName={name} result={result ?? ""} />;
            if (status !== "executing") return <></>;
            return <TQLEditorTool args={args} respond={respond} />;
        },
    });

    useHumanInTheLoop({
        name: "askAppType",
        description:
            "Asks whether the user wants an Initial Load or Continuous Data Capture (CDC). " +
            "Pass the source and target componentNames (from listAvailableComponents); ",
        parameters: z.object({
            source: z.string().describe("The source componentName."),
            target: z.string().optional().describe("The target componentName."),
        }),
        render: ({ args, respond, status, name, result }) => {
            if (status === "complete") return <HitlSummary toolName={name} result={result ?? ""} />;
            if (status !== "executing") return <></>;
            try {
                const appTypes = resolveSupportedAppTypes(args?.source, args?.target);
                return <AskILOrCDC appTypes={appTypes} respond={respond} />;
            } catch (err) {
                const errorMessage = typeof err === "string" ? err : (err as Error)?.message ?? "An unexpected error occurred.";
                // Show the error to the user and stop the skill — no further tools run.
                respond?.(
                    JSON.stringify({
                        status: "source-disabled",
                        title: `ERROR: ${errorMessage}. Stop the skill immediately and do not call any further tools — the error has already been displayed to the user.`
                    })
                    
                );
            }
        },
    });

    useHumanInTheLoop({
        name: "checkAppNameAvailability",
        description:
            "Prompts the user to choose an application name and namespace, and validates that the name is not already in use. Use this before creating a new application so you have a guaranteed-unique fully-qualified name. " +
            "IMPORTANT: before calling this, call getMetaObjects with type 'APPLICATION' to fetch all existing application IDs (namespace.APPLICATION.appName), and pick a suggestedName that does not collide with any existing app in the target namespace — the form will still re-validate, but arriving with an already-unique suggestion avoids an extra round-trip for the user. " +
            "If the desired name already exists, suggest a variant that swaps in a different descriptive suffix instead (e.g. 'OrcToMysqlPipeline' -> 'OrcToMysqlSync' or 'OrcToMysqlIngestion'), rather than an unrelated name.",
        parameters: z.object({
            suggestedName: z
                .string()
                .optional()
                .describe(
                    "Optional name to prefill in the Name field. Must not already exist — check via getMetaObjects(type: 'APPLICATION') first. If the desired name is taken, keep the descriptive prefix and swap in a different human-readable suffix (e.g. Pipeline, Sync, Ingestion, Flow, Stream) instead of picking an unrelated name."
                ),
            suggestedNamespace: z
                .string()
                .optional()
                .describe("Optional namespace to preselect; defaults to the user's namespace")
        }),
        render: ({ args, respond, status, name, result }) => {
            if (status === "complete") return <HitlSummary toolName={name} result={result ?? ""} />;
            if (status !== "executing") return <></>;
            return <NameAppCheckerTool args={args} respond={respond} />;
        },
    });

    useHumanInTheLoop({
        name: "configureSource",
        description:
            `Walks the user through configuring a Striim source (connection details → validate connection → pick tables → validate tables). Only call this for these database adapters: ${SUPPORTED_SOURCE_TYPES.join(", ")}. For any other source adapter (e.g. file/cloud-storage adapters like S3Reader, HDFSReader, GCSReader, ADLSReader), use showSimpleWizardForm instead. Call this AFTER askInitialLoadOrCDC has returned an ingestion mode and you know the sourceType. Returns a JSON payload describing the configured source; use it to generate the CREATE SOURCE TQL via showEditorForTQL.`,
        parameters: z.object({
            sourceType: z
                .enum(SUPPORTED_SOURCE_TYPES)
                .describe(`Striim adapter name. Must be one of: ${SUPPORTED_SOURCE_TYPES.join(", ")}.`),
            mode: z
                .enum(["Initial Load", "Change Data Capture", "Automated"])
                .describe("Output of askInitialLoadOrCDC."),
            targetComponentName: z
                .string()
                .optional()
                .describe("Component name of the target adapter (e.g. 'OutputMySQLDatabase', 'SnowflakeWriter'). Pass this when the target is already known — used to determine if auto-schema-conversion is supported for this source+target pair.")
        }),
        render: ({ args, respond, status, name, result }) => {
            if (status === "complete") return <HitlSummary toolName={name} result={result ?? ""} />;
            if (status !== "executing") return <></>;
            try {
                checkIfSourceEnabled(args?.sourceType);
                return <SourceConfiguratorTool args={args} respond={respond} />;
            } catch (err) {
                const errorMessage = typeof err === "string" ? err : (err as Error)?.message ?? "An unexpected error occurred.";
                // Show the error to the user and stop the skill — no further tools run.
                respond?.(
                    JSON.stringify({
                        status: "source-disabled",
                        title: `ERROR: ${errorMessage}. Stop the skill immediately and do not call any further tools — the error has already been displayed to the user.`
                    })
                );
            }
        },
    });

    useHumanInTheLoop({
        name: "showSimpleWizardForm",
        description:
            "Displays the simple source wizard form for file-based or cloud-storage adapters (e.g. S3Reader, HDFSReader, GCSReader, ADLSReader) that do not require a multi-step database connection flow. Use this instead of configureSource when the source adapter uses the base simple source wizard. Returns a JSON payload with the configured source model properties. Skip this tool when the user already supplied all required values — go straight to showEditorForTQL with those values. " +
            "IMPORTANT: call listAvailableComponents({ type: \"SOURCE\" }) first, match the source technology against the returned componentName / humanReadableName / adapter, and pass the matched entry's componentName — do not guess it.",
        parameters: z.object({
            componentName: z
                .string()
                .describe("The exact catalog Component Name resolved from listAvailableComponents({ type: \"SOURCE\" }), e.g. S3Reader, HDFSReader, GCSReader, ADLSReader."),
            appName: z
                .string()
                .describe("Fully-qualified application name in namespace.appName format, e.g. Admin.MyApp."),
            templateID: z
                .string()
                .optional()
                .describe("Optional topology template ID used to determine source/target pairing for contextual messages.")
        }),
        render: ({ args, respond, status, name, result }) => {
            if (status === "complete") return <HitlSummary toolName={name} result={result ?? ""} />;
            if (status !== "executing") return <></>;
            try {
                checkIfSourceEnabled(args?.componentName);
                return <ShowSimpleWizardFormTool args={args} respond={respond} />;
            } catch (err) {
                const errorMessage = typeof err === "string" ? err : (err as Error)?.message ?? "An unexpected error occurred.";
                // Show the error to the user and stop the skill — no further tools run.
                respond?.(
                    JSON.stringify({
                        status: "source-disabled",
                        title: `ERROR: ${errorMessage}. Stop the skill immediately and do not call any further tools — the error has already been displayed to the user.`
                    })
                );
            }
        },
    });

    // configureTarget disabled — every target configuration now goes through showBasicTargetForm.
    // useHumanInTheLoop({
    //     name: "configureTarget",
    //     description:
    //         `Walks the user through configuring a Striim target — connection details then connection validation. Supported target types: ${SUPPORTED_TARGET_TYPES}. Returns a JSON payload with the configured target form values; use it to generate the CREATE TARGET TQL via showEditorForTQL. Skip this tool when the user already supplied all required values (connection/auth details, table mappings) — go straight to showEditorForTQL with those values.`,
    //     parameters: z.object({
    //         targetType: z
    //             .string()
    //             .describe(
    //                 `Striim target adapter name. Must be one of the supported types: ${SUPPORTED_TARGET_TYPES}.`
    //             )
    //     }),
    //     render: ({ args, respond, status, name, result }) => {
    //         if (status === "complete") return <HitlSummary toolName={name} result={result ?? ""} />;
    //         if (status !== "executing") return <></>;
    //         try {
    //             checkIfTargetEnabled(args?.targetType);
    //             return <TargetConfiguratorTool args={args} respond={respond} />;
    //         } catch (err) {
    //             const errorMessage = typeof err === "string" ? err : (err as Error)?.message ?? "An unexpected error occurred.";
    //             // Show the error to the user and stop the skill — no further tools run.
    //             respond?.(
    //                 JSON.stringify({
    //                     status: "target-disabled",
    //                     title: `ERROR: ${errorMessage}. Stop the skill immediately and do not call any further tools — the error has already been displayed to the user.`
    //                 })
    //             );
    //         }
    //     }
    // });

    useHumanInTheLoop({
        name: "adapterNotFound",
        description:
            "Surfaces an 'adapter not found' error to the user and stops the current skill. Call this ONLY after every resolution tool has already been tried and exhausted (getCommonAdapters, listAvailableComponents, listPropertyTemplates/describePropertyTemplate) and the source or target still could not be resolved. Never call this speculatively, and never call any other tool after it returns — the error has already been displayed to the user.",
        parameters: z.object({
            adapterName: z.string().describe("The source/target technology or adapter name that could not be resolved."),
            componentType: z
                .enum(["SOURCE", "TARGET"])
                .optional()
                .describe("Whether the unresolved component was a source or a target."),
        }),
        render: ({ args, respond, status, name, result }) => {
            if (status === "complete") return <HitlSummary toolName={name} result={result ?? ""} />;
            if (status !== "executing") return <></>;
            const kind = args?.componentType ? args.componentType.toLowerCase() : "adapter";
            respond?.(
                JSON.stringify({
                    status: "adapter-not-found",
                    adapterName: args?.adapterName,
                    title: `ERROR: The ${kind} "${args?.adapterName}" is not available. Stop the skill immediately and do not call any further tools — the error has already been displayed to the user.`
                })
            );
            return <></>;
        },
    });

    useHumanInTheLoop({
        name: "showBasicTargetForm",
        description:
            `Displays the target configuration wizard form for target adapters that use the base simple target wizard. Returns a JSON payload with the configured target model properties. Skip this tool when the user already supplied all required values — go straight to showEditorForTQL with those values. ` +
            `IMPORTANT: call listAvailableComponents({ type: "TARGET" }) first, match the destination technology against the returned componentName / humanReadableName / adapter, and pass the matched entry's componentName — do not guess it.`,
        parameters: z.object({
            componentName: z
                .string()
                .describe(`The exact catalog Component Name resolved from listAvailableComponents({ type: "TARGET" }), e.g. OutputMySQLDatabase, SnowflakeWriter, OutputPostgresDatabase.`),
            appName: z
                .string()
                .describe("Fully-qualified application name in namespace.appName format, e.g. Admin.MyApp."),
            templateID: z
                .string()
                .optional()
                .describe("Optional topology template ID used to determine source/target pairing for contextual messages.")
        }),
        render: ({ args, respond, status, name, result }) => {
            if (status === "complete") return <HitlSummary toolName={name} result={result ?? ""} />;
            if (status !== "executing") return <></>;
            try {
                checkIfTargetEnabled(args?.componentName);
                return <ShowBasicTargetFormTool args={args} respond={respond} />;
            } catch (err) {
                const errorMessage = typeof err === "string" ? err : (err as Error)?.message ?? "An unexpected error occurred.";
                // Show the error to the user and stop the skill — no further tools run.
                respond?.(
                    JSON.stringify({
                        status: "target-disabled",
                        title: `ERROR: ${errorMessage}. Stop the skill immediately and do not call any further tools — the error has already been displayed to the user.`
                    })
                );
            }
        },
    });

     
};
